import React from 'react';
import { ArrowUpRightIcon, ArrowDownRightIcon } from 'lucide-react';
import type { LoanOfficer, MetricType, PeriodInfo } from '../../types';
import { ProgressBar } from '../ui/ProgressBar';
import { getProgressPercentage, calculateProjection, getProgressStatus } from '../../utils/calculations';
interface KanbanCardProps {
  loanOfficer: LoanOfficer;
  teamName: string;
  divisionName: string;
  period: PeriodInfo;
  metric: MetricType;
}
export function KanbanCard({
  loanOfficer,
  teamName,
  divisionName,
  period,
  metric
}: KanbanCardProps) {
  const current = metric === 'softPull' ? loanOfficer.softPullCount : loanOfficer.submittedCount;
  const goal = metric === 'softPull' ? loanOfficer.softPullGoal : loanOfficer.submittedGoal;
  const percentage = getProgressPercentage(current, goal);
  const projected = calculateProjection(current, period.daysElapsed, period.totalDays);
  const projectedPct = getProgressPercentage(projected, goal);
  const status = getProgressStatus(projectedPct);
  const isAboveGoal = projected >= goal;
  const statusColors: Record<string, string> = {
    'on-track': 'text-emerald-600 dark:text-emerald-400',
    'at-risk': 'text-amber-600 dark:text-amber-400',
    behind: 'text-red-600 dark:text-red-400'
  };
  return <div className="bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg p-4 hover:shadow-md transition-shadow duration-200" data-id="element-55">
      {/* Top row: Name + percentage */}
      <div className="flex items-start justify-between gap-3 mb-2" data-id="element-56">
        <h4 className="text-sm font-semibold text-gray-900 dark:text-white truncate leading-tight" data-id="element-57">
          {loanOfficer.name}
        </h4>
        <span className={`text-sm font-bold flex-shrink-0 ${statusColors[status]}`} data-id="element-58">
          {percentage}%
        </span>
      </div>

      {/* Team + Division */}
      <p className="text-xs text-gray-500 dark:text-gray-400 mb-3 truncate" data-id="element-59">
        {teamName} · {divisionName}
      </p>

      {/* Mini Progress Bar */}
      <div className="mb-3" data-id="element-60">
        <ProgressBar current={current} goal={goal} size="sm" showLabel={false} data-id="element-61" />
      </div>

      {/* Bottom row: Count + Projection */}
      <div className="flex items-center justify-between gap-3" data-id="element-62">
        <span className="text-sm font-semibold text-gray-900 dark:text-white" data-id="element-63">
          {current}
          <span className="text-gray-400 dark:text-gray-500 font-normal" data-id="element-64">
            {' '}
            / {goal}
          </span>
        </span>
        <div className="flex items-center gap-1.5 min-w-0 flex-shrink-0" data-id="element-65">
          {isAboveGoal ? <ArrowUpRightIcon className="w-3.5 h-3.5 text-emerald-500 dark:text-emerald-400 flex-shrink-0" data-id="element-66" /> : <ArrowDownRightIcon className="w-3.5 h-3.5 text-red-500 dark:text-red-400 flex-shrink-0" data-id="element-67" />}
          <span className="text-xs text-gray-500 dark:text-gray-400 font-medium" data-id="element-68">
            {projected >= goal ? 'On pace' : 'Behind'}
          </span>
        </div>
      </div>
    </div>;
}